import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import React from 'react';
import { message } from 'antd';
import { GlobalPropertiesService } from '../services/api/globalPropertiesService';
import { PropertyDefinition, PropertyDefinitionResponse, PropertySummary } from '../types/globalProperties';
import { apiClient } from '../services/apiClient';
import { Button, Tag, Space, Tooltip } from 'antd';
import {
  LinkOutlined,
  DatabaseOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Typography } from 'antd';
import { debounce } from '../utils/debounce';

const { Text } = Typography;

export interface PropertyTableRow {
  key: string;
  property_key: string;
  property_type: string;
  description?: string;
  option_catalog?: string;
  option_catalog_id?: string;
  dependencies_count: number;
  dependents_count: number;
  has_calculation: boolean;
  has_validation: boolean;
  has_state_actions: boolean;
  definition: PropertyDefinition;
}

interface UseGlobalPropertyTableProps {
  propertyKeys?: string[];
  onRowClick?: (_record: PropertyTableRow) => void;
  onPropertyKeyClick?: (_propertyKey: string) => void;
  selectedPropertyKey?: string | null;
  showSplitPane?: boolean;
}

export const useGlobalPropertyTable = ({
  propertyKeys,
  onRowClick,
  onPropertyKeyClick,
  selectedPropertyKey,
  showSplitPane: _showSplitPane = false
}: UseGlobalPropertyTableProps) => {
  const [properties, setProperties] = useState<Map<string, PropertyDefinition>>(new Map());
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [summaryProperties, setSummaryProperties] = useState<PropertySummary[]>([]);
  const [propertiesMetadata, setPropertiesMetadata] = useState<{ total: number; skip: number; limit: number } | null>(null);
  const [optionCatalogs, setOptionCatalogs] = useState<Array<{ id: string; name: string }>>([]);
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  // Stable key for propertyKeys so loadProperties only changes when filter set changes (not on new array ref)
  const propertyKeysRef = useRef(propertyKeys);
  propertyKeysRef.current = propertyKeys;
  const propertyKeysKey = useMemo(
    () => (propertyKeys && propertyKeys.length > 0 ? [...propertyKeys].sort().join(',') : ''),
    [propertyKeys]
  );

  // loadProperties reads search from ref (not deps) to avoid API call on every keystroke
  const loadProperties = useCallback(async (searchOverride?: string) => {
    const search = searchOverride ?? searchTextRef.current;
    setLoading(true);
    try {
      const propertiesMap = new Map<string, PropertyDefinition>();

      // ALWAYS use summary endpoint for table display (much faster than individual calls)
      {
        try {
          const response = await GlobalPropertiesService.listPropertyDefinitions({
            skip: 0,
            limit: 1000,
            search: search || undefined,
            summary: true  // Always use summary mode for table display (performs much better)
          });
          
          if (response.isSummary) {
            // Filter by propertyKeys if provided (client-side filtering after summary load)
            let filteredProperties = response.properties as PropertySummary[];
            const keys = propertyKeysRef.current && propertyKeysRef.current.length > 0 ? propertyKeysRef.current : null;
            if (keys) {
              const propertyKeysSet = new Set(keys);
              filteredProperties = filteredProperties.filter(p => propertyKeysSet.has(p.property_key));
            }
            
            // Store summary data for table display
            setSummaryProperties(filteredProperties);
            setPropertiesMetadata({
              total: filteredProperties.length,
              skip: response.skip,
              limit: response.limit
            });
            
            // Keep properties Map empty when using summary mode
            // Full definitions will be loaded on-demand when editing
            setProperties(new Map());
          } else {
            // Fallback: Handle full definitions if summary not available
            const items = response.properties as PropertyDefinitionResponse[];
            items.forEach((item: PropertyDefinitionResponse) => {
              if (item?.property_key && item?.definition) {
                propertiesMap.set(item.property_key, item.definition);
              } else if ((item as any)?.definition?.property_key) {
                const def = (item as any).definition as PropertyDefinition;
                propertiesMap.set(def.property_key, def);
              } else {
                console.warn('[useGlobalPropertyTable] Skipping invalid item:', item);
              }
            });
            
            // Clear summary data since we're using full definitions
            setSummaryProperties([]);
            setPropertiesMetadata(null);
            setProperties(propertiesMap);
          }
        } catch (error: unknown) {
          console.error('[useGlobalPropertyTable] Error calling listPropertyDefinitions:', error);
          const msg = error instanceof Error ? error.message : (error as { detail?: string })?.detail ?? 'Unknown error';
          void message.error(`Failed to load properties: ${msg}`);
        }
      }
    } catch (error: unknown) {
      console.error('Error loading properties:', error);
      void message.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [propertyKeysKey]);

  useEffect(() => {
    void loadProperties();
  }, [loadProperties]);

  // Debounce search-triggered reload; skip on initial mount (first useEffect handles that)
  const isInitialMount = useRef(true);
  const debouncedLoadOnSearch = useMemo(
    () =>
      debounce(() => {
        void loadProperties(searchTextRef.current);
      }, 400),
    [loadProperties]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debouncedLoadOnSearch();
  }, [searchText, debouncedLoadOnSearch]);

  // Load option catalogs for table display
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const response = await apiClient.get('/option-catalogs');
        if (Array.isArray(response.data)) {
          setOptionCatalogs(response.data.map((cat: any) => ({
            id: cat.id || cat.name,
            name: cat.name || cat.id
          })));
        }
      } catch (error) {
        console.warn('Failed to load option catalogs:', error);
        setOptionCatalogs([]);
      }
    };
    void loadCatalogs();
  }, []);

  // Helper function to extract all property keys from calculation_dependencies
  // Handles both old format (direct source_properties) and new format (named configurations)
  const extractPropertyKeysFromCalculationDependencies = useCallback((calcDeps: any): Set<string> => {
    const propertyKeys = new Set<string>();
    
    if (!calcDeps || typeof calcDeps !== 'object') {
      return propertyKeys;
    }

    // Handle old format: direct source_properties object
    if (calcDeps.source_properties && typeof calcDeps.source_properties === 'object') {
      if (Array.isArray(calcDeps.source_properties)) {
        // Array format: iterate through source properties
        calcDeps.source_properties.forEach((sourceProp: any) => {
          if (sourceProp.property_key) {
            propertyKeys.add(sourceProp.property_key);
          } else if (sourceProp.reference && sourceProp.property_index !== undefined) {
            // New format: reference + property_index
            const refConfig = calcDeps[sourceProp.reference];
            if (refConfig) {
              const refPropertyKeys = refConfig.property_keys || (refConfig.property_key ? [refConfig.property_key] : []);
              if (refPropertyKeys[sourceProp.property_index]) {
                propertyKeys.add(refPropertyKeys[sourceProp.property_index]);
              }
            }
          }
        });
      } else {
        // Object format: iterate through values
        Object.values(calcDeps.source_properties).forEach((sourceProp: any) => {
          if (sourceProp?.property_key) {
            propertyKeys.add(sourceProp.property_key);
          }
        });
      }
    }

    // Handle new format: named calculation configurations (calculation1, calculation2, etc.)
    Object.keys(calcDeps).forEach(key => {
      if (key.match(/^calculation\d+$/)) {
        const calcConfig = calcDeps[key];
        if (calcConfig?.source_properties) {
          const sourceProps = Array.isArray(calcConfig.source_properties) 
            ? calcConfig.source_properties 
            : Object.values(calcConfig.source_properties);
          
          sourceProps.forEach((sourceProp: any) => {
            if (sourceProp.property_key) {
              // Direct property_key (legacy)
              propertyKeys.add(sourceProp.property_key);
            } else if (sourceProp.reference && sourceProp.property_index !== undefined) {
              // New format: reference + property_index
              const refKey = sourceProp.reference;
              const refConfig = calcDeps[refKey];
              if (refConfig) {
                const refPropertyKeys = refConfig.property_keys || (refConfig.property_key ? [refConfig.property_key] : []);
                if (refPropertyKeys[sourceProp.property_index]) {
                  propertyKeys.add(refPropertyKeys[sourceProp.property_index]);
                }
              }
            }
          });
        }
      }
    });

    return propertyKeys;
  }, []);

  const tableData = useMemo((): PropertyTableRow[] => {
    // Build a map of all property keys to their calculation dependencies for efficient lookup
    const allPropertyDependenciesMap = new Map<string, Set<string>>();
    
    // First pass: collect all dependencies from all properties (both summary and full)
    const allPropertyKeysSet = new Set<string>();
    
    // Collect from summary properties
    summaryProperties.forEach(summary => {
      allPropertyKeysSet.add(summary.property_key);
    });
    
    // Collect from full properties
    properties.forEach((def, key) => {
      allPropertyKeysSet.add(key);
      const deps = extractPropertyKeysFromCalculationDependencies(def.calculation_dependencies);
      allPropertyDependenciesMap.set(key, deps);
    });
    
    // If we have summary data, we need to load full definitions to calculate dependencies correctly
    // For now, we'll recalculate from summary if full definitions aren't available
    // But ideally we should load full definitions when summary is used
    
    // Use summary data when available (from summary endpoint)
    if (summaryProperties.length > 0) {
      return summaryProperties.map((summary): PropertyTableRow => {
        // Get full definition if available (for row click/details and accurate dependency calculation)
        const fullDef = properties.get(summary.property_key);
        
        // Calculate dependencies from full definition if available, otherwise use summary count
        let dependenciesCount = summary.dependencies_count || 0;
        if (fullDef?.calculation_dependencies) {
          const deps = extractPropertyKeysFromCalculationDependencies(fullDef.calculation_dependencies);
          dependenciesCount = deps.size;
          allPropertyDependenciesMap.set(summary.property_key, deps);
        }
        
        // Calculate dependents by checking all other properties
        const dependentsCount = Array.from(allPropertyDependenciesMap.entries()).filter(([otherKey, deps]) => {
          return otherKey !== summary.property_key && deps.has(summary.property_key);
        }).length;
        
        // Look up option catalog name from loaded catalogs if not provided in summary
        const optionCatalogName = summary.option_catalog_name || 
          (summary.global_option_catalog_ref
            ? optionCatalogs.find(cat => cat.id === summary.global_option_catalog_ref)?.name
            : undefined);

        return {
          key: summary.property_key,
          property_key: summary.property_key,
          property_type: summary.property_type,
          description: summary.description || undefined,
          option_catalog: optionCatalogName || 
            (summary.global_option_catalog_ref ? `${summary.global_option_catalog_ref.substring(0, 8)}...` : undefined),
          option_catalog_id: summary.global_option_catalog_ref || undefined,
          dependencies_count: dependenciesCount,
          dependents_count: dependentsCount,
          has_calculation: summary.has_calculation,
          has_validation: summary.has_validation,
          has_state_actions: summary.has_state_actions,
          definition: fullDef || {
            property_key: summary.property_key,
            property_type: summary.property_type as any,
            description: summary.description || undefined
          } as PropertyDefinition
        };
      });
    }

    // Fallback: Use full properties map if summary not available
    const rows: PropertyTableRow[] = [];
    const allPropertyKeys = Array.from(properties.keys());

    properties.forEach((def, key) => {
      // Count dependencies - extract all property keys from calculation_dependencies
      const dependenciesSet = extractPropertyKeysFromCalculationDependencies(def.calculation_dependencies);
      const dependenciesCount = dependenciesSet.size;

      // Count dependents (properties that depend on this one)
      // Check all other properties to see if they reference this property key
      const dependentsCount = allPropertyKeys.filter(otherKey => {
        if (otherKey === key) return false; // Don't count self
        const otherDef = properties.get(otherKey);
        if (!otherDef?.calculation_dependencies) return false;
        
        const otherDependencies = extractPropertyKeysFromCalculationDependencies(otherDef.calculation_dependencies);
        return otherDependencies.has(key);
      }).length;

      // Look up option catalog name from loaded catalogs
      const optionCatalogName = def.global_option_catalog_ref
        ? optionCatalogs.find(cat => cat.id === def.global_option_catalog_ref)?.name
        : undefined;

      rows.push({
        key,
        property_key: key,
        property_type: def.property_type,
        description: def.description,
        option_catalog: optionCatalogName || 
          (def.global_option_catalog_ref ? `${def.global_option_catalog_ref.substring(0, 8)}...` : undefined),
        option_catalog_id: def.global_option_catalog_ref,
        dependencies_count: dependenciesCount,
        dependents_count: dependentsCount,
        has_calculation: !!def.calculation_dependencies,
        has_validation: !!def.validation_rules && Object.keys(def.validation_rules).length > 0,
        has_state_actions: !!def.state_actions && (Array.isArray(def.state_actions) ? def.state_actions.length > 0 : true),
        definition: def
      });
    });

    return rows;
  }, [summaryProperties, properties, optionCatalogs]);

  const filteredData = useMemo(() => {
    if (!searchText) return tableData;

    const lowerSearch = searchText.toLowerCase();
    return tableData.filter(row =>
      row.property_key.toLowerCase().includes(lowerSearch) ||
      row.description?.toLowerCase().includes(lowerSearch) ||
      row.property_type.toLowerCase().includes(lowerSearch)
    );
  }, [tableData, searchText]);

  const columns = useMemo(() => [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      width: 240,
      render: (text: string) => (
        <Button
          type="link"
          onClick={() => {
            onPropertyKeyClick?.(text);
          }}
          style={{ padding: 0 }}
        >
          <Text code>{text}</Text>
        </Button>
      ),
      sorter: (a: PropertyTableRow, b: PropertyTableRow) =>
        a.property_key.localeCompare(b.property_key),
      defaultSortOrder: 'ascend' as const
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      width: 70,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
      filters: Array.from(new Set(tableData.map(row => row.property_type))).map(type => ({
        text: type,
        value: type
      })),
      onFilter: (value: any, record: PropertyTableRow) => record.property_type === value
    },
    {
      title: 'Dependencies',
      dataIndex: 'dependencies_count',
      key: 'dependencies_count',
      width: 90,
      render: (count: number) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          <LinkOutlined /> {count}
        </Tag>
      ),
      sorter: (a: PropertyTableRow, b: PropertyTableRow) =>
        a.dependencies_count - b.dependencies_count
    },
    {
      title: 'Dependents',
      dataIndex: 'dependents_count',
      key: 'dependents_count',
      width: 90,
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'default'}>
          <LinkOutlined /> {count}
        </Tag>
      ),
      sorter: (a: PropertyTableRow, b: PropertyTableRow) =>
        a.dependents_count - b.dependents_count
    },
    {
      title: 'Relationships',
      key: 'relationships',
      render: (_: any, record: PropertyTableRow) => (
        <Space size="small">
          {record.has_calculation && (
            <Tooltip title="Has calculation dependencies">
              <Tag icon={<CalculatorOutlined />} color="purple">
                Calc
              </Tag>
            </Tooltip>
          )}
          {record.has_validation && (
            <Tooltip title="Has validation rules">
              <Tag icon={<CheckCircleOutlined />} color="green">
                Valid
              </Tag>
            </Tooltip>
          )}
          {record.has_state_actions && (
            <Tooltip title="Has state actions">
              <Tag icon={<InfoCircleOutlined />} color="orange">
                State
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Option Catalog',
      dataIndex: 'option_catalog',
      key: 'option_catalog',
      render: (text: string, record: PropertyTableRow) => {
        if (!text) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={record.option_catalog_id}>
            <Tag color="cyan" icon={<DatabaseOutlined />}>
              {text}
            </Tag>
          </Tooltip>
        );
      }
    }
  ], [tableData, onPropertyKeyClick]);

  const handleRowClick = useCallback((record: PropertyTableRow) => {
    onRowClick?.(record);
  }, [onRowClick]);

  const handleFormSuccess = useCallback(() => {
    void loadProperties();
    if (selectedPropertyKey) {
      // Reload the selected property
      GlobalPropertiesService.getPropertyDefinition(selectedPropertyKey)
        .then((response) => {
          const newProperties = new Map(properties);
          newProperties.set(selectedPropertyKey, response.definition);
          setProperties(newProperties);
        })
        .catch((error) => {
          console.error('Error reloading property:', error);
        });
    }
  }, [loadProperties, selectedPropertyKey, properties]);

  return {
    // State
    properties,
    loading,
    searchText,
    setSearchText,
    summaryProperties,
    propertiesMetadata,
    optionCatalogs,
    // Data
    tableData,
    filteredData,
    columns,
    // Functions
    loadProperties,
    handleRowClick,
    handleFormSuccess
  };
};
